import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  useTheme,
} from "@mui/material";
import { Route, Check, Pill, Droplet, ChevronRight } from "lucide-react";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import { pool } from "../../../constants";
import * as c from "../../../constants";
import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import ResultsProgressBar from "./ResultsProgressBar";
import LabProcessingInfo from "./LabProcessingInfo";
import { OnboardingDialog as CycleOnboardingDialog } from "../../CockpitWelcome/PeriodTracker/CyclePeriod/SectionCyclePeriod";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import MedicationPanel from "../../CockpitWelcome/Medication/MedicationPanel";

// Maps composite radio value → API payload (copied from WaitingResults.jsx)
const RADIO_PAYLOADS = {
  cycletracker:     { profile: "cycletracker" },
  cycletracker_hbc: { profile: "cycletracker", status: "hormonal_birth_control" },
  perimenopause:    { profile: "perimenopause" },
  postmenopause:    { profile: "postmenopause" },
};

function actionsCardSx(theme, accentColor) {
  return {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

function ActionItem({ accentColor, sx, children }) {
  const theme = useTheme();
  return (
    <Box sx={{ ...actionsCardSx(theme, accentColor), p: 3, ...sx }}>
      {children}
    </Box>
  );
}

export default function FirstLoginStep() {
  const theme = useTheme();
  const { state } = useAuth();
  const { t, changeLanguage } = useTranslation();

  const [visitDate, setVisitDate] = useState(null);
  const [labInfoOpen, setLabInfoOpen] = useState(false);

  // --- Cycle tracker state (copied from WaitingResults.jsx — do not change WaitingResults.jsx) ---
  const [femaleProfile, setFemaleProfile] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);
  const [femaleProfileLoading, setFemaleProfileLoading] = useState(true);
  const [coreOpen, setCoreOpen] = useState(false);
  const [cycleOpen, setCycleOpen] = useState(false);

  // --- Medication state (copied from WaitingResults.jsx — do not change WaitingResults.jsx) ---
  const [medOpen, setMedOpen] = useState(false);
  const [medSaved, setMedSaved] = useState(false);
  const [noMedication, setNoMedication] = useState(false);

  const userId = state?.user?.id;
  const isFemale = state?.user?.gender === "F";

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  useEffect(() => {
    if (!userId) return;

    axios
      .get(`${c.API_URL}/api/current-lab-visit/`)
      .then((res) => setVisitDate(res.data?.visit_date ?? null))
      .catch(() => setVisitDate(null));
  }, [userId]);

  // Check for existing female profile record (copied from WaitingResults.jsx)
  useEffect(() => {
    if (!isFemale) {
      setFemaleProfileLoading(false);
      return;
    }
    axios
      .get(`${c.API_URL}/api/female/profile`)
      .then((res) => {
        const profile = res.data?.profile;
        const status  = res.data?.status;
        if (profile) {
          const radioValue =
            profile === "cycletracker" && status === "hormonal_birth_control"
              ? "cycletracker_hbc"
              : profile;
          setFemaleProfile(radioValue);
          setProfileSaved(true);
        }
      })
      .catch(() => {}) // 404 = no record → show radio options
      .finally(() => setFemaleProfileLoading(false));
  }, [isFemale]);

  const handleFemaleProfileConfirm = async () => {
    try {
      await axios.post(`${c.API_URL}/api/female/profile`, RADIO_PAYLOADS[femaleProfile]);
      if (femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc") {
        setCycleOpen(false);
        setCoreOpen(true);
      } else {
        setProfileSaved(true);
      }
    } catch (err) {
      console.error("Failed to save female profile:", err);
    }
  };

  const showCycleTrackerItem = isFemale && !femaleProfileLoading;
  const questionRows = [
    ...(showCycleTrackerItem
      ? [{ key: "cycle", icon: <Droplet size={24} color={theme.palette.primary.main} />, label: t("onb_wait_cycle_row"), done: profileSaved, onClick: () => setCycleOpen(true) }]
      : []),
    { key: "med", icon: <Pill size={24} color={theme.palette.primary.main} />, label: t("onb_wait_med_row"), done: medSaved || noMedication, onClick: () => setMedOpen(true) },
  ];

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext={t("onbsub_brandtext")}
      content={
        <Grid container sx={{ mt: 4, justifyContent: 'center' }}>
          <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 0 } }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t("onb_results_wait_overline")}
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {t("onb_results_wait_title")}
              </Typography>
            </Box>

            <ResultsProgressBar visitDate={visitDate} onInfoClick={() => setLabInfoOpen(true)} />

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" fontWeight={400} textAlign="center" sx={{ mb: 3 }}>
              {t("onb_results_wait_actions_header")}
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderRadius: 4,
                overflow: 'hidden',
                mb: 3,
                ...(theme.palette.mode === 'light'
                  ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
                  : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
                      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
              }}
            >
              {questionRows.map(({ key, icon, label, done, onClick }, idx) => (
                <Box key={key}>
                  {idx > 0 && <Divider sx={{ mx: 3 }} />}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    onClick={onClick}
                    sx={{
                      px: 3, py: 2.5,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      '&:hover': { bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C800' },
                    }}
                  >
                    {icon}
                    <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 400 }}>
                      {label}
                    </Typography>
                    {done
                      ? <Check size={20} strokeWidth={2.5} color={theme.palette.traffic.green} />
                      : <ChevronRight size={20} color={theme.palette.text.secondary} />}
                  </Stack>
                </Box>
              ))}
            </Box>

            <ActionItem accentColor={theme.palette.quartiary.C300}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t("onb_email_header")}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                {t("onb_email_text")}
              </Typography>
            </ActionItem>
          </Grid>

          <CycleOnboardingDialog
            open={coreOpen}
            onClose={() => setCoreOpen(false)}
            onSuccess={() => setProfileSaved(true)}
          />

          <CustomDialog
            open={labInfoOpen}
            onClose={() => setLabInfoOpen(false)}
            title={t("onb_results_wait_lab_header")}
            content={<LabProcessingInfo />}
          />

          <CustomDialog
            open={cycleOpen}
            onClose={() => setCycleOpen(false)}
            title={t("onb_tracker_header")}
            content={
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 400, mb: 2 }}>
                  {t("onb_tracker2")}
                  {!profileSaved && <> {t("onb_tracker_select")}</>}
                </Typography>

                {profileSaved ? (
                  <Typography variant="subtitle1" sx={{ fontWeight: 400, color: "success.main" }}>
                    {t("onb_tracker_done")}
                  </Typography>
                ) : (
                  <>
                    <RadioGroup
                      value={femaleProfile}
                      onChange={(e) => setFemaleProfile(e.target.value)}
                      sx={{ mb: 2 }}
                    >
                      <FormControlLabel value="cycletracker"     control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt1")}</Typography>} />
                      <FormControlLabel value="cycletracker_hbc" control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt3")}</Typography>} />
                      <FormControlLabel value="perimenopause"    control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt4")}</Typography>} />
                      <FormControlLabel value="postmenopause"    control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt2")}</Typography>} />
                    </RadioGroup>

                    {(femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc") && (
                      <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
                        <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker3")}</Typography>
                        <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker4")}</Typography>
                        <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker5")}</Typography>
                      </Box>
                    )}

                    <IconButton
                      size="large"
                      disabled={!femaleProfile}
                      onClick={handleFemaleProfileConfirm}
                      sx={{ display: "flex", flexDirection: "row", gap: 1.5, bgcolor: "primary.C100", borderRadius: 7, px: 4, py: 1.5, "&:hover": { bgcolor: "primary.C200" }, "&.Mui-disabled": { opacity: 0.4 } }}
                    >
                      {(femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc")
                        ? <Route size={32} color={theme.palette.secondary.dark} />
                        : <Check size={32} color={theme.palette.secondary.dark} />
                      }
                      <Typography variant="caption" sx={{ color: theme.palette.secondary.dark, fontWeight: 500, letterSpacing: 0.3 }}>
                        {(femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc") ? t("onb_tracker6") : t("onb_tracker_confirm")}
                      </Typography>
                    </IconButton>
                  </>
                )}
              </Box>
            }
          />

          <CustomDialog
            open={medOpen}
            onClose={() => { setMedOpen(false); setMedSaved(true); }}
            title={t("onb_med_header")}
            content={
              <Box>
                {!noMedication && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 400, mb: 2 }}>
                      {t("onb_med_intro")}
                    </Typography>
                    <MedicationPanel />
                  </>
                )}

                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Checkbox
                      checked={noMedication}
                      onChange={(e) => setNoMedication(e.target.checked)}
                    />
                  }
                  label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_med_none")}</Typography>}
                />
              </Box>
            }
          />
        </Grid>
      }
    />
  );
}
